import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useApi() {
  const { getToken } = useAuth();
  return useMemo(() => {
    const call = async (method, url, data, opts = {}) => {
      const token = await getToken();
      const res = await axios({ method, url: `${API}${url}`, data, headers: { Authorization: `Bearer ${token}` }, ...opts });
      return res.data;
    };
    return {
      get: (u, o) => call("get", u, undefined, o),
      post: (u, d, o) => call("post", u, d, o),
      put: (u, d) => call("put", u, d),
      patch: (u, d) => call("patch", u, d),
      del: (u) => call("delete", u),
      blob: async (u) => call("get", u, undefined, { responseType: "blob" }),
    };
  }, [getToken]);
}

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return e?.message || "Something went wrong";
};

const Ctx = createContext(null);
export const useWorkspace = () => useContext(Ctx);

export function WorkspaceProvider({ children }) {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [groupId, setGroupIdRaw] = useState(() => localStorage.getItem("fo_group") || "");
  const [farmId, setFarmIdRaw] = useState(() => localStorage.getItem("fo_farm") || "all");

  const refresh = useCallback(async () => {
    try {
      setMe(await api.get("/me"));
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  const allGroups = me?.groups || [];
  const groups = allGroups.filter((g) => !g.archived);
  const allFarms = me?.farms || [];
  const multiGroup = groups.length > 1;
  const effectiveGroupId = groups.find((g) => g.id === groupId) ? groupId : multiGroup && groupId === "all" ? "all" : groups[0]?.id || "";
  const farms = allFarms.filter((f) => effectiveGroupId === "all" || f.group_id === effectiveGroupId).filter((f) => !f.archived);
  const single = farms.length === 1;
  const effectiveFarmId = single ? farms[0].id : farmId === "all" || farms.find((f) => f.id === farmId) ? farmId : "all";
  const farm = farms.find((f) => f.id === effectiveFarmId) || null;
  const group = groups.find((g) => g.id === (farm?.group_id || effectiveGroupId)) || null;

  const setGroupId = (g) => { localStorage.setItem("fo_group", g); setGroupIdRaw(g); setFarmId("all"); };
  const setFarmId = (f) => { localStorage.setItem("fo_farm", f); setFarmIdRaw(f); };
  const can = (p) => (me?.user?.perms || []).includes(p);
  const role = me?.user?.role;
  const isManager = ["admin", "executive", "operations", "farm_manager"].includes(role);
  const mods = farm?.effective_modules || null;

  const value = { me, error, refresh, groups, allGroups, farms, allFarms, farm, group, groupId: effectiveGroupId, farmId: effectiveFarmId,
    setGroupId, setFarmId, can, role, isManager, mods, multiGroup, user: me?.user };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const NZD = (v, dp = 0) => (v === null || v === undefined || Number.isNaN(v) ? "—" :
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v));
export const num = (v, dp = 1) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("en-NZ", { maximumFractionDigits: dp }));
export const nzDate = (s) => {
  if (!s) return "—";
  const d = s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
  return d.toLocaleDateString("en-NZ", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Pacific/Auckland" });
};
export const nzDateTime = (s) => (s ? new Date(s).toLocaleString("en-NZ", { timeZone: "Pacific/Auckland", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
export const todayNZ = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland" }).format(new Date());
export const monthNZ = () => todayNZ().slice(0, 7);
export const monthLabel = (m) => (m ? new Date(`${m}-01T00:00:00`).toLocaleDateString("en-NZ", { month: "long", year: "numeric" }) : "");
export const shiftMonth = (m, n) => { const d = new Date(`${m}-01T00:00:00`); d.setMonth(d.getMonth() + n); return d.toISOString().slice(0, 7); };
export const LABELS = {
  owner_operated: "Owner operated", contract_milked: "Contract milked", sharemilking_50_50: "50/50 sharemilking", other: "Other",
  operator: "Operator", sharemilker: "Sharemilker", management_oversight: "Management / oversight",
  admin: "Administrator", executive: "Executive", operations: "Operations", farm_manager: "Farm manager", contractor: "Contractor / sharemilker", staff: "Staff", grazier: "Grazier (portal only)", pending: "Awaiting access", grazing: "Grazing (graziers' stock)",
  farm_owner: "Farm owner", tcg: "TCG",
};
export const label = (k) => LABELS[k] || (k ? String(k).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "—");
